// Modules
import { Link } from 'react-router';
import React from 'react';
import _ from 'lodash';
import moment from 'moment';

import Company from 'services/data/company';
import { COMPANY_SERVICES, CC_DOMAIN } from 'constants';
import { Card } from 'components/content-containers/cards';
import { EditButton, DeleteButton, RestoreButton } from 'components/widgets/buttons/buttons';
import { MetadataTable, MetadataItem } from 'components/content-containers/tables';
import { PageContent } from 'components/page';
import { getSCLink } from 'utils/app-utils';
import { noIcon, yesIcon } from 'components/widgets/badges/badges';
import Address from 'services/data/address';
import AgreementLink from 'components/models/agreement/agreement-link';
import CompanyLink from 'components/models/company/company-link';
import CopyToClipboard from 'components/widgets/icons/copy-to-clipboard';
import Person from 'services/data/person';
import PersonLink from 'components/models/person/person-link';
import PhoneNumber from 'services/data/phone-number';
import SVG from 'components/widgets/icons/svg';
import * as actions from '../store/actions';


function formatHours(open, start, end) {
  if (!open) return 'Closed';
  if (open && !start) return 'All Day';

  const startTime = moment(start, 'HH:mm').format('h:mm a');
  const endTime = moment(end, 'HH:mm').format('h:mm a');

  return `${startTime} - ${endTime}`;
}

// Component
export default function CompanySummary({ currentUser, id }) {
  const { data: company, refetch } = actions.useCompany(id);
  const isLead = Person.isLead(currentUser);
  const emails = Person.getOrderedContactInfo(company.emailAddresses);
  const numbers = Person.getOrderedContactInfo(company.phoneNumbers);
  const addresses = Person.getOrderedContactInfo(company.addresses);
  const client = company.companyType === 'Client';
  const hasTeam = company.team && company.team.id;
  const hasParent = company.parentCompany && company.parentCompany.id;
  const hasTech = company.primaryTech && company.primaryTech.id;
  const apps = company.externalAppIds || {};
  const sc = apps.screenConnect;
  const archived = company.status === 'Archived';

  function update(changes) {
    return Company.update({ id, ...changes })
      .then(() => refetch());
  }

  return (
    <PageContent className="bg-medium-blue-grey">
      <div className="company-summary flex flex-equal-children">
        <div className="page-column">
          <Card className="company-details">
            { archived &&
              <div className="card-header disabled-card-header">
                <div>
                  <SVG src="svg/archived.svg" className="no-margin" />
                  <span className="margin-left">Deleted</span>
                </div>
                <div className="card-header-actions">
                  <RestoreButton onClick={() => update({ status: 'Active' })} />
                </div>
              </div>
            }
            <div className="summary-wrapper">
              { !archived &&
                <div className="card-icon-buttons">
                  <Link to={`/companies/${company.id}/edit`}><EditButton /></Link>
                  { !client && <DeleteButton onClick={() => update({ status: 'Archived' })} /> }
                  { client && isLead && <DeleteButton disabled="true" title="Cannot delete a client, deactivate agreements first!" /> }
                </div>
              }
              <MetadataTable>
                <MetadataItem label="Nickname">{company.nickname}</MetadataItem>
                <MetadataItem label="Company Type">{company.companyType}</MetadataItem>
                <MetadataItem label="Company Email">
                  <CopyToClipboard component="span" copyText={`company-${company.id}@${CC_DOMAIN}`} tooltip="Copy the company email">
                    <span>company-{company.id}@{CC_DOMAIN}</span>
                    <SVG src="svg/clipboard.svg" className="clipboard" />
                  </CopyToClipboard>
                </MetadataItem>
                { client && hasTeam &&
                  <MetadataItem label="Team">
                    <span className="color-dot" style={{ backgroundColor: company.team.color }}></span>
                    {company.team.name}
                  </MetadataItem>
                }
                { client && hasTech &&
                  <MetadataItem label="Primary Tech">
                    <PersonLink person={company.primaryTech} />
                  </MetadataItem>
                }
                { client &&
                  <MetadataItem label="Default Agreement">
                    <AgreementLink agreement={company.defaultAgreement} />
                  </MetadataItem>
                }
                { hasParent &&
                  <MetadataItem label="Parent Company">
                    <CompanyLink company={company.parentCompany} />
                  </MetadataItem>
                }
                { client &&
                  <MetadataItem label="User Escalator">
                    { company.hasUserEscalator && <span>{yesIcon}Yes</span> }
                    { !company.hasUserEscalator && <span>{noIcon}No</span> }
                  </MetadataItem>
                }
                { client &&
                  <MetadataItem label="Disable Autofill">
                    { company.disableAutofill && <span>Yes, triage autofill is disabled</span> }
                    { !company.disableAutofill && <span>No, triage autofill is enabled</span> }
                  </MetadataItem>
                }
                { client &&
                  <MetadataItem label="Payment Method">
                    {company.paymentMethod}
                    { company.paymentMethod === 'ACH' &&
                      <SVG src="svg/payment-ach.svg" className="payment-ach company-icon" />
                    }
                  </MetadataItem>
                }
                { client &&
                  <MetadataItem label="Invoice Method">
                    {company.invoiceMethod}
                    { company.invoiceMethod === 'Mail' &&
                      <SVG src="svg/mail.svg" className="mail company-icon" />
                    }
                  </MetadataItem>
                }
                { client &&
                  <MetadataItem label="Billing Emails">
                    { _.map(_.filter(company.billingEmails, { contactType: 'company' }), email =>
                      <p key={email.id}><span className="deemphasize">Company</span><br />{email.email}</p>
                    ) }
                    { _.map(_.filter(company.billingEmails, { contactType: 'person' }), email =>
                      <p key={email.id}><span className="deemphasize">{Person.fullName(email.contact)}</span><br />{email.email}</p>
                    ) }
                  </MetadataItem>
                }
                <MetadataItem label="Company GUID">
                  <CopyToClipboard component="span" copyText={company.uuid} tooltip="Copy the company GUID">
                    <span>{company.uuid}</span>
                    <SVG src="svg/clipboard.svg" className="clipboard" />
                  </CopyToClipboard>
                </MetadataItem>
                { client &&
                  <MetadataItem label="Connected Apps">
                    { _.map(COMPANY_SERVICES, service => {
                      const hasService = apps[service];

                      return (
                        <div key={service} className={hasService ? '' : 'deemphasize'}>
                          {hasService ? yesIcon : noIcon}
                          {_.startCase(service)}
                          {hasService && `: ${hasService}`}
                          {hasService && service === 'zabbix' && ` (${company.zabbixGroup})`}
                        </div>
                      );
                    }) }
                  </MetadataItem>
                }
                { sc &&
                  <MetadataItem label="SC Installers">
                    <div><a href={getSCLink(company.name, sc, 'msi')}>Windows MSI</a></div>
                    <div><a href={getSCLink(company.name, sc, 'exe')}>Windows EXE</a></div>
                    <div><a href={getSCLink(company.name, sc, 'pkg')}>Mac</a></div>
                    <div><a href={getSCLink(company.name, sc, 'deb')}>Ubuntu</a></div>
                  </MetadataItem>
                }
                { client &&
                  <MetadataItem label="Business Hours">
                    <table className="table table-sm table-borderless">
                      <tr>
                        <td className="py-0 pl-0 collapse-width">Sunday:</td>
                        <td className="py-0">
                          {formatHours(company.sundayOpen, company.sundayBusinessHoursStart, company.sundayBusinessHoursEnd)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-0 pl-0 collapse-width">Monday:</td>
                        <td className="py-0">
                          {formatHours(company.mondayOpen, company.mondayBusinessHoursStart, company.mondayBusinessHoursEnd)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-0 pl-0 collapse-width">Tuesday:</td>
                        <td className="py-0">
                          {formatHours(company.tuesdayOpen, company.tuesdayBusinessHoursStart, company.tuesdayBusinessHoursEnd)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-0 pl-0 collapse-width">Wednesday:</td>
                        <td className="py-0">
                          {formatHours(company.wednesdayOpen, company.wednesdayBusinessHoursStart, company.wednesdayBusinessHoursEnd)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-0 pl-0 collapse-width">Thursday:</td>
                        <td className="py-0">
                          {formatHours(company.thursdayOpen, company.thursdayBusinessHoursStart, company.thursdayBusinessHoursEnd)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-0 pl-0 collapse-width">Friday:</td>
                        <td className="py-0">
                          {formatHours(company.fridayOpen, company.fridayBusinessHoursStart, company.fridayBusinessHoursEnd)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-0 pl-0 collapse-width">Saturday:</td>
                        <td className="py-0">
                          {formatHours(company.saturdayOpen, company.saturdayBusinessHoursStart, company.saturdayBusinessHoursEnd)}
                        </td>
                      </tr>
                    </table>
                  </MetadataItem>
                }
              </MetadataTable>
            </div>
          </Card>
        </div>
        <div className="page-column">
          <Card className="company-contact-details">
            <MetadataTable>
              <MetadataItem label="Email Addresses">
                { _.map(emails, email => {
                  const modifiers = [];

                  if (email.primary) modifiers.push('primary');
                  if (email.billingCompanies && email.billingCompanies.length) modifiers.push('receives invoices');

                  return (
                    <p key={email.id}>
                      <span className="deemphasize">
                        {email.type} { !!modifiers.length && <span>({_.join(modifiers, ', ')})</span> }
                      </span><br />
                      <a target="_blank" href={`mailto:${email.email}`}>{email.email}</a>
                    </p>
                  );
                }) }
              </MetadataItem>
              <MetadataItem label="Phone Numbers">
                { _.map(numbers, number =>
                  <p key={number.id}>
                    <span className="deemphasize">{number.type}</span><br />
                    <a target="_blank" href={`tel:${number.number}`}>{PhoneNumber.format(number.number)}</a>
                  </p>
                ) }
              </MetadataItem>
              <MetadataItem label="Addresses">
                { _.map(addresses, address =>
                  <p key={address.id}>
                    <span className="deemphasize">
                      {address.type} { address.billing && <span>(billing)</span> }
                    </span><br />
                    <a target="_blank" href={`https://www.google.com/maps/place/${Address.getGoogleMapsPlace(address)}`}>
                      { address.name && <span>{address.name}<br /></span> }
                      {address.address}<br />
                      { address.line2 && <span>{address.line2}<br /></span> }
                      {address.city}, {address.state} {address.zip}
                    </a>
                  </p>
                ) }
              </MetadataItem>
            </MetadataTable>
          </Card>
        </div>
      </div>
    </PageContent>
  );
}



// WEBPACK FOOTER //
// ./app/routes/company/sub-routes/company-summary.js